<template>
  <div class="template-shell">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <main v-else class="pellicule">
      <!-- Grain + vignette : ambiance cinéma (décoratif, fixe) -->
      <div class="film-grain" aria-hidden="true"></div>
      <div class="film-vignette" aria-hidden="true"></div>

      <!-- Habillage fixe : titre + compteur de photogrammes -->
      <header class="film-hud">
        <div class="hud-brand">
          <span class="hud-eyebrow">Pellicule</span>
          <span class="hud-title">{{ exhibitName || 'Exposition' }}</span>
        </div>
        <div v-if="totalFrames > 0" class="hud-counter" aria-live="polite">
          <span class="hud-counter-current">{{ padIndex(currentFrame) }}</span>
          <span class="hud-counter-sep">/</span>
          <span class="hud-counter-total">{{ padIndex(totalFrames) }}</span>
        </div>
      </header>

      <!-- Viewport épinglé : le scroll vertical devient un travelling horizontal -->
      <section ref="pinRef" class="film-viewport">
        <div v-if="loading" class="film-state">Développement de la pellicule…</div>

        <div v-else ref="stripRef" class="film-strip">
          <!-- Photogramme d'ouverture : carton-titre -->
          <article class="frame frame--intro">
            <div class="frame-window intro-window">
              <span class="intro-eyebrow">Une exposition par {{ userName || '—' }}</span>
              <h1 class="intro-title">{{ exhibitName || 'Exposition' }}</h1>
              <span class="intro-hint">
                Faites défiler pour dérouler la pellicule
                <span class="intro-arrow" aria-hidden="true">→</span>
              </span>
            </div>
            <div class="frame-meta">
              <span class="frame-number">00</span>
              <span class="frame-label">Générique</span>
            </div>
          </article>

          <!-- Photogrammes : une ResourceMedia par ressource -->
          <article
            v-for="(resource, index) in resources"
            :key="resource.id || resource.resource_id || index"
            class="frame"
            role="button"
            tabindex="0"
            :aria-label="`Ouvrir le photogramme ${index + 1}`"
            @click="openLightbox(index)"
            @keydown.enter.prevent="openLightbox(index)"
            @keydown.space.prevent="openLightbox(index)"
          >
            <div class="frame-window">
              <ResourceMedia
                :resource="resource"
                :url="resourceUrls[index]"
                context="thumb"
                fit="cover"
                :active="centeredIndex === index + 1"
              />
            </div>
            <div class="frame-meta">
              <span class="frame-number">{{ padIndex(index + 1) }}</span>
              <span class="frame-label">{{ frameLabel(resource) }}</span>
            </div>
          </article>

          <!-- Photogramme de fin (uniquement s'il y a des ressources) -->
          <article v-if="resources.length" class="frame frame--outro">
            <div class="frame-window outro-window">
              <span class="outro-mark" aria-hidden="true">✕</span>
              <span class="outro-text">Fin</span>
            </div>
            <div class="frame-meta">
              <span class="frame-number">{{ padIndex(resources.length + 1) }}</span>
              <span class="frame-label">Bobine terminée</span>
            </div>
          </article>
        </div>
      </section>
    </main>

    <ResourceLightbox
      :resources="resources"
      :urls="resourceUrls"
      v-model:selectedIndex="selectedIndex"
    />
  </div>
</template>

<script setup>
import { computed, defineProps, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';
import { getMediaType } from '../../utils/mediaType';
import { cleanText, padIndex } from '../../utils/galleryText';
import ResourceMedia from '../../components/gallery/ResourceMedia.vue';
import ResourceLightbox from '../../components/gallery/ResourceLightbox.vue';

gsap.registerPlugin(ScrollTrigger);

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  homeImage,
  homePage,
  id,
  loading,
  resourceUrls,
  resources,
  userName,
} = useGalleryTemplate(props);

const pinRef = ref(null);
const stripRef = ref(null);
const selectedIndex = ref(null);

// centeredIndex : index du cadre le plus proche du centre de l'écran
// (0 = carton-titre, 1..N = photogrammes, N+1 = carton de fin).
// Pilote le compteur ET l'activation vidéo (:active) du photogramme centré.
const centeredIndex = ref(0);
const totalFrames = computed(() => resources.value.length);
// Le compteur affiché ignore le carton de fin (plafonné à NN / NN).
const currentFrame = computed(() => Math.min(centeredIndex.value, totalFrames.value));

const TYPE_LABELS = {
  image: 'Photogramme',
  video: 'Séquence',
  audio: 'Piste sonore',
  doc: 'Document',
  other: 'Photogramme',
};

const frameLabel = (resource) => cleanText(resource?.title) || TYPE_LABELS[getMediaType(resource?.name)] || 'Photogramme';

const openLightbox = (index) => {
  selectedIndex.value = index;
};

/* --------------------------- travelling horizontal -------------------------- */
let scrollTrigger = null;
let scrollTween = null;

// Géométrie mesurée une seule fois par (re)construction / refresh — les
// largeurs de cadres sont statiques (clamp CSS), inutile de lire le DOM à
// chaque tick de scrub.
let frameCenters = [];
let viewportHalf = 0;
let travelDistance = 0;

const getScrollAmount = () => {
  const strip = stripRef.value;
  const viewport = pinRef.value;
  if (!strip || !viewport) return 0;
  return Math.max(0, strip.scrollWidth - viewport.clientWidth);
};

// Mesure le centre de chaque cadre en coordonnées de la pellicule
// (offsetLeft ignore le transform du travelling : mesure stable).
const measureFrames = () => {
  const strip = stripRef.value;
  const viewport = pinRef.value;
  if (!strip || !viewport) {
    frameCenters = [];
    return;
  }
  viewportHalf = viewport.clientWidth / 2;
  travelDistance = getScrollAmount();
  frameCenters = Array.from(strip.children).map(
    (el) => el.offsetLeft + el.offsetWidth / 2
  );
};

// Détermine le cadre le plus proche du centre de l'écran à partir de la seule
// progression du travelling — aucune lecture DOM par tick.
const updateActiveFrame = (progress = 0) => {
  if (!frameCenters.length) {
    centeredIndex.value = 0;
    return;
  }
  const target = viewportHalf + progress * travelDistance;
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < frameCenters.length; i += 1) {
    const distance = Math.abs(frameCenters[i] - target);
    if (distance > bestDistance) break; // centres triés : la distance remonte
    bestDistance = distance;
    best = i;
  }
  centeredIndex.value = best;
};

const killScrollTrigger = () => {
  if (scrollTrigger) {
    scrollTrigger.kill(true);
    scrollTrigger = null;
  }
  if (scrollTween) {
    scrollTween.kill();
    scrollTween = null;
  }
  if (stripRef.value) {
    gsap.set(stripRef.value, { clearProps: 'transform' });
  }
};

const buildScrollTrigger = () => {
  killScrollTrigger();
  if (homePage.value || loading.value) return;
  const strip = stripRef.value;
  const viewport = pinRef.value;
  if (!strip || !viewport) return;

  measureFrames();
  updateActiveFrame(0);

  // Pas assez de contenu pour défiler : la pellicule reste statique.
  if (travelDistance <= 0) return;

  scrollTween = gsap.to(strip, {
    x: () => -getScrollAmount(),
    ease: 'none',
  });

  scrollTrigger = ScrollTrigger.create({
    animation: scrollTween,
    trigger: viewport,
    start: 'top top',
    end: () => `+=${getScrollAmount()}`,
    pin: true,
    scrub: 0.6,
    anticipatePin: 1,
    invalidateOnRefresh: true,
    onUpdate: (self) => updateActiveFrame(self.progress),
    // Re-mesure après chaque refresh (resize, chargement de médias…).
    onRefresh: (self) => {
      measureFrames();
      updateActiveFrame(self.progress);
    },
  });
};

// (Re)construit dès que la galerie est prête ou que le jeu de ressources change.
watch(
  () => [homePage.value, loading.value, resources.value.length],
  () => {
    nextTick(() => buildScrollTrigger());
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  killScrollTrigger();
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Oswald:wght@400;600;700&display=swap');

.template-shell {
  min-height: 100vh;
  background: #08080a;
  color: #f2efe9;
}

.pellicule {
  --film-bg: #08080a;
  --film-cell: #17171a;
  --film-hole: #050506;
  --film-accent: #e6b25e;
  --film-fg: #f2efe9;
  --perf-h: clamp(11px, 1.7vh, 18px);
  --perf-inset: clamp(12px, 2.6vh, 30px);
  position: relative;
  min-height: 100vh;
  background: var(--film-bg);
  color: var(--film-fg);
  overflow: hidden;
}

/* --------------------------------- HUD --------------------------------- */
.film-hud {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 7;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: clamp(14px, 2.4vw, 28px) clamp(18px, 3.2vw, 44px);
  pointer-events: none;
  mix-blend-mode: difference;
}

.hud-brand {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.hud-eyebrow {
  font-family: 'Bebas Neue', 'Oswald', 'Arial Narrow', sans-serif;
  font-size: 13px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--film-accent);
}

.hud-title {
  font-family: 'Oswald', 'Arial Narrow', 'Helvetica Neue', sans-serif;
  font-weight: 600;
  font-stretch: condensed;
  font-size: clamp(15px, 2.2vw, 22px);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 46vw;
}

.hud-counter {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-family: 'Oswald', 'Bebas Neue', 'Arial Narrow', sans-serif;
  font-stretch: condensed;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.hud-counter-current {
  font-size: clamp(30px, 5vw, 52px);
  font-weight: 700;
  line-height: 0.9;
  color: var(--film-accent);
}

.hud-counter-sep {
  font-size: clamp(18px, 3vw, 30px);
  opacity: 0.5;
}

.hud-counter-total {
  font-size: clamp(18px, 3vw, 30px);
  opacity: 0.7;
}

/* ------------------------------ film viewport ------------------------------ */
.film-viewport {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: var(--film-bg);
}

.film-state {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font-family: 'Oswald', 'Arial Narrow', sans-serif;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #9a968c;
  font-size: 15px;
}

/* --------------------------------- strip --------------------------------- */
.film-strip {
  position: relative;
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.6vw, 26px);
  width: max-content;
  height: 100%;
  padding: 0 clamp(24px, 6vw, 90px);
  background: var(--film-cell);
  will-change: transform;
}

/* Perforations (trous de bobine) le long des deux bords */
.film-strip::before,
.film-strip::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: var(--perf-h);
  z-index: 2;
  pointer-events: none;
  background-image: repeating-linear-gradient(
    to right,
    var(--film-hole) 0,
    var(--film-hole) 18px,
    transparent 18px,
    transparent 36px
  );
}

.film-strip::before {
  top: var(--perf-inset);
}

.film-strip::after {
  bottom: var(--perf-inset);
}

/* --------------------------------- frames --------------------------------- */
.frame {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: clamp(240px, 38vw, 460px);
  height: min(66vh, 560px);
  cursor: pointer;
  outline: none;
}

.frame[role='button']:focus-visible .frame-window {
  box-shadow: 0 0 0 2px var(--film-accent);
}

.frame-window {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.frame-window :deep(.resource-media) {
  transition: transform 600ms ease;
}

.frame:hover .frame-window :deep(.resource-media) {
  transform: scale(1.04);
}

.frame-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-family: 'Oswald', 'Bebas Neue', 'Arial Narrow', sans-serif;
  font-stretch: condensed;
  text-transform: uppercase;
}

.frame-number {
  font-size: clamp(15px, 1.8vw, 20px);
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--film-accent);
  font-variant-numeric: tabular-nums;
}

.frame-label {
  font-size: 11px;
  letter-spacing: 0.24em;
  color: #8f8b81;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* --------------------------- carton-titre (intro) --------------------------- */
.frame--intro {
  width: min(86vw, 720px);
  cursor: default;
}

.intro-window {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: clamp(14px, 2.4vh, 26px);
  padding: clamp(24px, 5vw, 60px);
  background: linear-gradient(135deg, #101013 0%, #050506 100%);
  border-color: rgba(255, 255, 255, 0.08);
}

.intro-eyebrow {
  font-family: 'Bebas Neue', 'Oswald', 'Arial Narrow', sans-serif;
  font-size: clamp(12px, 1.4vw, 15px);
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--film-accent);
}

.intro-title {
  margin: 0;
  font-family: 'Oswald', 'Arial Narrow', 'Helvetica Neue', sans-serif;
  font-weight: 700;
  font-stretch: condensed;
  font-size: clamp(38px, 7vw, 92px);
  line-height: 0.92;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #fff;
  overflow-wrap: anywhere;
}

.intro-hint {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: 'Oswald', 'Arial Narrow', sans-serif;
  font-size: clamp(12px, 1.4vw, 15px);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #b9b5ab;
}

.intro-arrow {
  display: inline-block;
  color: var(--film-accent);
  animation: nudge 1.5s ease-in-out infinite;
}

/* --------------------------- carton de fin (outro) -------------------------- */
.frame--outro {
  width: clamp(200px, 26vw, 320px);
  cursor: default;
}

.outro-window {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: linear-gradient(135deg, #101013 0%, #050506 100%);
}

.outro-mark {
  font-size: clamp(34px, 5vw, 54px);
  color: var(--film-accent);
  opacity: 0.85;
}

.outro-text {
  font-family: 'Oswald', 'Bebas Neue', 'Arial Narrow', sans-serif;
  font-stretch: condensed;
  font-size: clamp(16px, 2vw, 22px);
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: #cfcbc1;
}

/* --------------------------- overlays décoratifs --------------------------- */
.film-grain {
  position: fixed;
  inset: 0;
  z-index: 6;
  pointer-events: none;
  opacity: 0.07;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 120px 120px;
  animation: grain 1.4s steps(5) infinite;
}

.film-vignette {
  position: fixed;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  background: radial-gradient(ellipse at center, transparent 52%, rgba(0, 0, 0, 0.6) 100%);
}

@keyframes grain {
  0% { background-position: 0 0; }
  25% { background-position: -30px 20px; }
  50% { background-position: 24px -18px; }
  75% { background-position: -18px -26px; }
  100% { background-position: 20px 22px; }
}

@keyframes nudge {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(6px); }
}

@media (prefers-reduced-motion: reduce) {
  .film-grain,
  .intro-arrow {
    animation: none;
  }
}

/* -------------------------------- responsive ------------------------------- */
@media (max-width: 900px) {
  .frame {
    width: clamp(200px, 66vw, 340px);
    height: min(60vh, 460px);
  }

  .frame--intro {
    width: min(88vw, 480px);
  }

  .hud-title {
    max-width: 40vw;
  }
}

@media (max-width: 560px) {
  .film-strip {
    gap: 10px;
    padding: 0 18px;
  }

  .frame {
    width: 74vw;
    height: 56vh;
  }

  .frame--intro {
    width: 88vw;
  }

  .hud-eyebrow {
    letter-spacing: 0.3em;
  }

  .hud-title {
    max-width: 52vw;
    letter-spacing: 0.08em;
  }
}
</style>
